import { useCallback, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useCursor } from './cursor.ts';

/* ==========================================================================
   The interaction every time-bound instrument in the kit shares.

   Hover reports the instant under the pointer to the shared cursor. Click
   pins the playhead there. Drag brushes a range and, on release, zooms every
   bound panel to it — direct manipulation stages, the release commits, and
   the two are visibly different states.

   The keyboard reaches all of it: ←/→ step one sample, ⇧←/⇧→ step ten,
   Home/End jump to the window's edges, Esc drops the pin.
   ========================================================================== */

export interface Brush {
  from: number;
  to: number;
}

/** `x` for a time inside a window, in px across `width`. */
export function xScale(from: number, to: number, width: number) {
  const span = to - from || 1;
  return (t: number) => ((t - from) / span) * width;
}

export function usePlotSurface({
  stepMs,
  brushable = true,
  padL = 0,
  padR = 0,
}: {
  /** One sample's worth of time — what ←/→ move by. */
  stepMs: number;
  brushable?: boolean;
  /* THE GUTTERS ARE NOT THE PLOT.

     The label gutters live inside the same element as the traces — one
     element keeps one crosshair, one focus ring and one drag — so the
     pointer arithmetic has to subtract them. Without this, hovering the
     lane names reports a time before the window's start and the crosshair
     lands a gutter's width off the sample it is quoting. */
  padL?: number;
  padR?: number;
}) {
  const cursor = useCursor();
  const boxRef = useRef<HTMLElement | null>(null);
  const [brush, setBrush] = useState<Brush | null>(null);
  const anchor = useRef<number | null>(null);
  const moved = useRef(false);

  const setBox = useCallback((el: HTMLElement | null) => {
    boxRef.current = el;
  }, []);

  const timeAt = useCallback(
    (clientX: number): number | null => {
      const el = boxRef.current;
      if (!el) return null;
      const r = el.getBoundingClientRect();
      const plotW = r.width - padL - padR;
      if (plotW <= 0) return null;
      const f = Math.max(0, Math.min(1, (clientX - r.left - padL) / plotW));
      return cursor.from + f * (cursor.to - cursor.from);
    },
    [cursor.from, cursor.to, padL, padR],
  );

  /* THE GUTTERS ARE INERT.

     They are inside the same element as the plot, so without this a click on
     a lane's name pins the playhead at the window's left edge and a click on
     a threshold label pins it at the right — a cursor jump the operator did
     not ask for, from a control they did not know was one. A drag that
     STARTED in the plot may still run out into a gutter: that is a brush to
     the window's edge, which is exactly what dragging off the end means. */
  const inPlot = useCallback(
    (clientX: number): boolean => {
      const el = boxRef.current;
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return clientX >= r.left + padL && clientX <= r.right - padR;
    },
    [padL, padR],
  );

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      const dragging = anchor.current !== null;
      if (!dragging && !inPlot(e.clientX)) {
        cursor.setHover(null);
        return;
      }
      const t = timeAt(e.clientX);
      if (t === null) return;
      cursor.setHover(t);
      if (anchor.current !== null) {
        moved.current = true;
        setBrush({ from: Math.min(anchor.current, t), to: Math.max(anchor.current, t) });
      }
    },
    [cursor, timeAt, inPlot],
  );

  const onPointerLeave = useCallback(() => {
    if (anchor.current === null) cursor.setHover(null);
  }, [cursor]);

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      if (e.button !== 0 || !inPlot(e.clientX)) return;
      const t = timeAt(e.clientX);
      if (t === null) return;
      anchor.current = t;
      moved.current = false;
      if (brushable) e.currentTarget.setPointerCapture(e.pointerId);
    },
    [brushable, timeAt, inPlot],
  );

  const onPointerUp = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      const start = anchor.current;
      anchor.current = null;
      setBrush(null);
      const t = timeAt(e.clientX);
      if (start === null || t === null) return;
      const el = boxRef.current;
      const px = el
        ? (Math.abs(t - start) / (cursor.to - cursor.from || 1)) *
          Math.max(1, el.getBoundingClientRect().width - padL - padR)
        : 0;
      /* Under four pixels of travel is a click that wobbled, not a range. */
      if (brushable && moved.current && px >= 4) {
        cursor.zoomTo(Math.min(start, t), Math.max(start, t));
      } else {
        cursor.setPlayhead(t);
      }
    },
    [brushable, cursor, timeAt, padL, padR],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      const mult = e.shiftKey ? 10 : 1;
      switch (e.key) {
        case 'ArrowLeft':
          cursor.setPlayhead(
            Math.max(cursor.from, (cursor.playhead ?? cursor.to) - stepMs * mult),
          );
          break;
        case 'ArrowRight':
          cursor.setPlayhead(
            Math.min(cursor.to, (cursor.playhead ?? cursor.from) + stepMs * mult),
          );
          break;
        case 'Home':
          cursor.setPlayhead(cursor.from);
          break;
        case 'End':
          cursor.setPlayhead(cursor.to);
          break;
        case 'Escape':
          cursor.setPlayhead(null);
          cursor.setHover(null);
          break;
        default:
          return;
      }
      e.preventDefault();
    },
    [cursor, stepMs],
  );

  return {
    brush,
    setBox,
    handlers: {
      onPointerMove,
      onPointerLeave,
      onPointerDown,
      onPointerUp,
      onKeyDown,
    },
  };
}
